import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";

const emptyEvent = {
  title: "",
  description: "",
  city_id: "",
  venue_name: "",
  venue_address: "",
  pickup_label: "",
  pickup_lat: "",
  pickup_lng: "",
  dropoff_lat: "",
  dropoff_lng: "",
  starts_at: "",
  shuttle_price: 0,
  capacity: 30,
  active: true,
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(emptyEvent);

  const load = () => {
    Promise.all([api.get("/admin/events"), api.get("/admin/shuttle-tickets"), api.get("/admin/cities")])
      .then(([e, t, c]) => {
        setEvents(Array.isArray(e.data) ? e.data : []);
        setTickets(Array.isArray(t.data) ? t.data : []);
        const cityList = Array.isArray(c.data) ? c.data : [];
        setCities(cityList);
        setForm((current) => current.city_id ? current : { ...current, city_id: cityList[0]?.id || "" });
      })
      .catch((err) => toast.error(formatApiError(err)));
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => setForm((current) => ({ ...current, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        pickup_lat: Number(form.pickup_lat),
        pickup_lng: Number(form.pickup_lng),
        dropoff_lat: Number(form.dropoff_lat),
        dropoff_lng: Number(form.dropoff_lng),
        shuttle_price: Number(form.shuttle_price),
        capacity: Number(form.capacity),
      };
      if (form.id) await api.put(`/admin/events/${form.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Evenement enregistre");
      setForm({ ...emptyEvent, city_id: cities[0]?.id || "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AdminShell title="Evenements">
      <div className="admin-page-header">
        <div>
          <h1 className="responsive-title font-heading text-3xl font-black tracking-tighter sm:text-4xl">Evenements, shuttle et tickets</h1>
          <p className="text-sm text-muted-foreground">Navettes aeroport, ecoles, stades, conferences et tickets clients.</p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
        <div className="space-y-4">
          <Card className="p-5">
            <h2 className="font-heading text-xl font-black">{form.id ? "Modifier evenement" : "Creer evenement shuttle"}</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div><Label>Titre</Label><Input value={form.title} onChange={set("title")} /></div>
              <div>
                <Label>Ville</Label>
                <select value={form.city_id} onChange={set("city_id")} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-foreground">
                  {cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}
                </select>
              </div>
              <div><Label>Lieu</Label><Input value={form.venue_name} onChange={set("venue_name")} /></div>
              <div><Label>Adresse lieu</Label><Input value={form.venue_address} onChange={set("venue_address")} /></div>
              <div><Label>Depart label</Label><Input value={form.pickup_label} onChange={set("pickup_label")} /></div>
              <div><Label>Date/heure</Label><Input type="datetime-local" value={form.starts_at} onChange={set("starts_at")} /></div>
              <div><Label>Depart lat</Label><Input type="number" value={form.pickup_lat} onChange={set("pickup_lat")} /></div>
              <div><Label>Depart lng</Label><Input type="number" value={form.pickup_lng} onChange={set("pickup_lng")} /></div>
              <div><Label>Destination lat</Label><Input type="number" value={form.dropoff_lat} onChange={set("dropoff_lat")} /></div>
              <div><Label>Destination lng</Label><Input type="number" value={form.dropoff_lng} onChange={set("dropoff_lng")} /></div>
              <div><Label>Prix place</Label><Input type="number" value={form.shuttle_price} onChange={set("shuttle_price")} /></div>
              <div><Label>Capacite</Label><Input type="number" value={form.capacity} onChange={set("capacity")} /></div>
            </div>
            <div className="mt-3">
              <Label>Description</Label>
              <textarea
                value={form.description}
                onChange={set("description")}
                rows={3}
                className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <label className="mt-3 flex items-center justify-between rounded-2xl bg-white/5 p-3 text-sm font-bold">
              Actif
              <input type="checkbox" checked={Boolean(form.active)} onChange={set("active")} />
            </label>
            <Button onClick={save} className="mt-4 w-full">{form.id ? "Sauvegarder" : "Creer l'evenement"}</Button>
          </Card>

          <div className="grid gap-3 md:grid-cols-2">
            {events.map((event) => (
              <Card key={event.id} className="p-4" data-testid={`admin-event-${event.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Badge tone={event.active ? "success" : "danger"}>{event.active ? "Actif" : "Off"}</Badge>
                    <h3 className="mt-2 font-heading text-xl font-black">{event.title}</h3>
                    <div className="text-xs text-muted-foreground">{event.city_name} - {event.venue_name}</div>
                    <div className="mt-1 font-mono text-xs">{new Date(event.starts_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-black">{formatCDF(event.shuttle_price)}</div>
                    <div className="text-[10px] text-muted-foreground">{event.reserved_seats || 0}/{event.capacity} places</div>
                  </div>
                </div>
                <Button variant="outline" className="mt-3 w-full text-xs" onClick={() => setForm({ ...event, starts_at: toDateTimeLocal(event.starts_at) })}>Modifier</Button>
              </Card>
            ))}
          </div>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-black">Tickets</h2>
            <Badge tone="primary">{tickets.length}</Badge>
          </div>
          <div className="mt-3 max-h-[680px] space-y-2 overflow-y-auto">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="rounded-2xl bg-white/5 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-bold">{ticket.event_title}</div>
                    <div className="font-mono text-xs text-primary">{ticket.code}</div>
                    <div className="text-[11px] text-muted-foreground">{ticket.customer_name} - {ticket.customer_phone}</div>
                  </div>
                  <Badge tone="success">{ticket.status}</Badge>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{ticket.seats} place(s) - {formatCDF(ticket.amount)}</div>
              </div>
            ))}
            {tickets.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">Aucun ticket</div>}
          </div>
        </Card>
      </div>
    </AdminShell>
  );
}
